import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import Link from 'gatsby-link';
import rehypeReact from 'rehype-react';
import PageToc from '../components/PageToc/PageToc';
import SiteNav from '../components/SiteNav/SiteNav';
import ICONS from '../components/icons/icons';
import LanguageSelect from '../components/LanguageSelect/LanguageSelect';
import YoutubeVideo from '../components/YoutubeVideo/YoutubeVideo';
import QuickStartPdfLink from '../components/QuickStartPdfLink/QuickStartPdfLink';
import { getSessionData } from '../components/sessionStorage/sessionStorage';
import getTitle from '../components/getTitle/getTitle';
import storeCourseUrl from '../components/storeCourseUrl/storeCourseUrl';
import { getParam } from '../components/helpers/helpers';
import SupportEmail from '../components/SupportEmail/SupportEmail';

// eslint-disable-next-line new-cap
const renderAst = new rehypeReact({
  createElement: React.createElement,
  components: { 'youtube-video': YoutubeVideo, 'quick-link': QuickStartPdfLink, 'support-email': SupportEmail },
}).Compiler;

class HelpDoc extends Component {
  static cleanHash = (hash) => {
    function lower(string) {
      return string.toLowerCase();
    }
    return hash.replace(/[A-Z]+/g, lower);
  };

  // helpDoc state tracks:
  // Window width for conditional rendering.
  // A boolean for determining whether mobile nav is expanded or not.
  state = {
    windowWidth: null,
    toggleMenuOpen: false,
  };

  // we set windowWidth on componentDidMount to avoid errors when Gatsby compiles
  componentDidMount() {
    // const { language } = this.props.data.site.siteMetadata.storageKeys;

    this.setState({
      windowWidth: window.innerWidth,
      pageTitle: getParam(location, 'title') || this.props.data.site.siteMetadata.title,
    });
    window.addEventListener('resize', this.handleResize);

    const courseKey = getParam(this.props.location, 'course');
    const courseTitle = getParam(this.props.location, 'title');

    if (courseKey !== false && courseTitle === null) {    
      getTitle(this.props.location, (newTitle) => { this.setState({ pageTitle: newTitle }); });
    }
    if (courseTitle !== false && getSessionData('courseUrl') === null) {
      storeCourseUrl(courseKey);
    }

    const { hash } = this.props.location;
    // Hash values get converted to a Uniform Resource Identifier by react-router.
    // We run the reverse when reading it.
    // this prevents errors with glyphs for languages such as Japanese, Arabic, Chinese, etc.
    const decodedHash = decodeURI(hash);
    // if not a hash, scroll page to top:
    if (hash.length === 0) {
      document.documentElement.scrollTop = 0;
    } else {
      const element = document.querySelector(HelpDoc.cleanHash(decodedHash));
      try {
        element.scrollIntoView({ behavior: 'instant', block: 'start', inline: 'nearest' });
      } catch (error) {
        console.error(hash);
      }
    }

    const eightByEight = () => {
      // eslint-disable-next-line no-underscore-dangle
      window.__8x8Chat = {
        uuid: 'script_1391945935ca76b5bb0f824.24056202',
        tenant: 'b3hmb3JkdW5pdmVyc2l0eTAx',
        channel: 'ELT Cross Platform',
        domain: 'https://vcc-eu9.8x8.com',
        path: '/.',
        buttonContainerId: '__8x8-chat-button-container-script_1391945935ca76b5bb0f824.24056202',
        align: 'right',
      };

      const insertDiv = document.querySelector('.doc-page-content-text div');
      const script = document.createElement('script');
      // eslint-disable-next-line no-underscore-dangle
      script.src = `${window.__8x8Chat.domain + window.__8x8Chat.path}/CHAT/common/js/chat.js`;
      script.async = true;

      insertDiv.appendChild(script);
    };

    const { markdownRemark } = this.props.data;
    const isContactPage = markdownRemark.frontmatter.icon === 'contact';

    // if (isContactPage) eightByEight();
    eightByEight();
  }
  // updating scroll position to make changes take effect when clicking through history:
  componentWillReceiveProps(nextProps) {
    // If, we're on the same page as before but the hash has changed:
    if (
      nextProps.location.pathname === this.props.location.pathname &&
      nextProps.location.hash !== this.props.location.hash
    ) {
      const { hash } = nextProps.location;
      // Hash values get converted to a Uniform Resource Identifier by react-router.
      // We run the reverse when reading it.
      // this prevents errors with glyphs for languages such as Japanese, Arabic, Chinese, etc.
      const decodedHash = decodeURI(hash);
      if (decodedHash.length > 0) {
        // scroll to new hash element if a hash still exists:
        try {
          const element = document.querySelector(HelpDoc.cleanHash(decodedHash));
          element.scrollIntoView({ behavior: 'instant', block: 'start', inline: 'nearest' });
        } catch (error) {
          console.error(hash);
        }
      } else if (this.state.windowWidth >= 760) {
        // if there is no longer a hash, scroll it to the top.
        // depending on window width it could be either the .doc-page-content div
        // or the html element that needs scrolling:
        const pageContent = document.querySelector('.doc-page-content');
        pageContent.scrollTop = 0;
      } else {
        document.documentElement.scrollTop = 0;
      }
    }
  }

  // clean up event listener
  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  // passed down to SiteNav
  toggleMenu = (state) => {
    this.setState({
      toggleMenuOpen: state,
    });
  };

  // called whenever window resizes
  handleResize = () => {
    this.setState({
      windowWidth: window.innerWidth,
    });
    // I should probably throttle this or use mutation observer
  };

  // We use this opportunity to map only the required data to a simpler object
  filteredData = Array.from(this.props.data.allMarkdownRemark.edges).map(item => ({
    slug: item.node.fields.slug,
    langKey: item.node.fields.langKey,
    shortTitle: item.node.frontmatter.shortTitle,
  }));

  render() {
    const { markdownRemark, site } = this.props.data;
    const { langKey } = this.props.pathContext;
    const { windowWidth } = this.state;
    const isContactPage = markdownRemark.frontmatter.icon === 'contact';
    // filter the headings to only provide h1-3 to pageToc component:
    const filteredHeadings = Array.from(markdownRemark.headings).filter(item => item.depth < 4);
    return (
      <div className="doc-page">
        <Helmet title={`${this.state.pageTitle}: ${markdownRemark.frontmatter.title}`}>
          <html lang={langKey} />
          <body className="page-doc" />
        </Helmet>

        <header className="doc-page-header">
          <h1 className="doc-page-header-home">
            <Link to={langKey === 'en' ? '/' : `/${markdownRemark.fields.langKey}/`}>
              {/* {this.state.windowWidth >= 760 ? <span>{site.siteMetadata.title}</span> : ''} */}
              <span className="course">{this.state.pageTitle} </span>
              <span className="hands">Help & support</span>
            </Link>
            {site.siteMetadata.languages.langs.length > 1 && (
              <LanguageSelect
                languages={site.siteMetadata.languages.langs}
                current={langKey}
                pathname={this.props.location.pathname}
                search={this.props.location.search}
              />
            )}
          </h1>

          {windowWidth && (
            <SiteNav
              links={this.filteredData}
              windowWidth={windowWidth}
              toggleMenuOpen={this.state.toggleMenuOpen}
              toggleMenu={this.toggleMenu}
            />
          )}
        </header>

        <div className="doc-page-content-container">
          {this.state.windowWidth >= 760 && <PageToc headings={filteredHeadings} />}

          <h1 className="doc-page-content-heading">
            <img
              className="doc-page-content-heading-image"
              src={ICONS[markdownRemark.frontmatter.icon]}
              alt=""
            />
            {markdownRemark.frontmatter.title}
          </h1>

          <article className="doc-page-content">
            <div className={`doc-page-content-text reading-texts ${isContactPage ? 'contact' : ''}`}>

              {renderAst(markdownRemark.htmlAst)}

              <div id="__8x8-chat-button-container-script_1391945935ca76b5bb0f824.24056202" className="eightByEight" />

            </div>
          </article>
        </div>
      </div>
    );
  }
}

export default HelpDoc;

HelpDoc.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.shape({
      html: PropTypes.string.isRequired,
      headings: PropTypes.arrayOf(PropTypes.shape({
        depth: PropTypes.number.isRequired,
        value: PropTypes.string.isRequired,
      })).isRequired,
      frontmatter: PropTypes.shape({
        date: PropTypes.string,
        title: PropTypes.string.isRequired,
        icon: PropTypes.string,
      }),
    }),
    site: PropTypes.shape({
      siteMetadata: PropTypes.shape({
        title: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    allMarkdownRemark: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.shape({
        node: PropTypes.shape({
          fields: PropTypes.shape({
            langKey: PropTypes.string.isRequired,
            slug: PropTypes.string.isRequired,
          }).isRequired,
          frontmatter: PropTypes.shape({
            order: PropTypes.number.isRequired,
            shortTitle: PropTypes.isRequired,
          }).isRequired,
        }),
      })).isRequired,
    }).isRequired,
  }).isRequired,
  pathContext: PropTypes.shape({
    langKey: PropTypes.string.isRequired,
    path: PropTypes.string,
  }),
  location: PropTypes.shape({
    hash: PropTypes.string,
    pathname: PropTypes.string,
  }),
};

export const pageQuery = graphql`
  query BlogPostByPath($path: String!, $langKey: String!) {
    site {
      siteMetadata {
        title
        languages {
          langs {
            slug
            title
          }
        }

        storageKeys {
          language
        }
      }
    }
    allMarkdownRemark(
      filter: { fields: { langKey: { eq: $langKey } } }
      sort: { fields: [frontmatter___order], order: ASC }
    ) {
      edges {
        node {
          fields {
            langKey
            slug
          }
          frontmatter {
            title
            shortTitle
            order
          }
        }
      }
    }
    markdownRemark(fields: { slug: { eq: $path } }) {
      html
      htmlAst
      headings {
        depth
        value
      }
      frontmatter {
        date(formatString: "MMMM DD, YYYY")
        title
        icon
      }
      fields {
        langKey
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/templates/doc-page.js